import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApplicationData } from '../LoanApplicationFlow';
import { formatRand, quoteLoan } from '../../../utils/loanPricing';
import './Step5Review.css';

interface Step5TermsAndConditionsProps {
  data: ApplicationData;
  onUpdate: (data: Partial<ApplicationData>) => void;
  onSubmit: () => void;
  onBack: () => void;
  onEditStep: (step: number) => void;
  isSubmitting: boolean;
}

function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('27') && digits.length === 11) {
    return `+27 ${digits.slice(2, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  if (digits.length === 10 && digits.startsWith('0')) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  return phone;
}

function formatSaId(id: string): string {
  const digits = id.replace(/\D/g, '');
  if (digits.length === 13) {
    return `${digits.slice(0, 6)} ${digits.slice(6, 10)} ${digits.slice(10)}`;
  }
  return id;
}

type ReviewFieldProps = { label: string; value: string };

const ReviewField: React.FC<ReviewFieldProps> = ({ label, value }) => (
  <div>
    <span className="review-field__label">{label}</span>
    <div className="review-field__value">{value}</div>
  </div>
);

type ReviewCardProps = {
  title: string;
  tone: 'personal' | 'bank' | 'institution';
  onEdit: () => void;
  children: React.ReactNode;
  fullWidth?: boolean;
};

const ReviewCard: React.FC<ReviewCardProps> = ({ title, tone, onEdit, children, fullWidth }) => (
  <div className={`review-card review-card--${tone}${fullWidth ? ' review-step__grid--full' : ''}`}>
    <div className="review-card__head">
      <div className="review-card__title-wrap">
        <div className={`review-card__icon review-card__icon--${tone}`}>
          {tone === 'personal' && (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          )}
          {tone === 'bank' && (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 21h18" />
              <path d="M5 21V7l7-4 7 4v14" />
              <path d="M9 21v-6h6v6" />
            </svg>
          )}
          {tone === 'institution' && (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
              <path d="M6 12v5c3 3 9 3 12 0v-5" />
            </svg>
          )}
        </div>
        <h3 className="review-card__title">{title}</h3>
      </div>
      <div className="review-card__actions">
        <span className="review-card__verified">Verified</span>
        <button type="button" className="review-card__edit" onClick={onEdit}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
          </svg>
          Edit
        </button>
      </div>
    </div>
    <div className="review-card__body">{children}</div>
  </div>
);

const Step5TermsAndConditions: React.FC<Step5TermsAndConditionsProps> = ({
  data,
  onUpdate,
  onSubmit,
  onBack,
  onEditStep,
  isSubmitting,
}) => {
  const [agreements, setAgreements] = useState({
    termsAccepted: data.termsAccepted,
    privacyAccepted: data.privacyAccepted,
    creditCheckConsent: data.creditCheckConsent,
    infoTruthful: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [openedDocs, setOpenedDocs] = useState({ terms: false, privacy: false });

  const quote = quoteLoan(data.loanAmount);
  const accountHolder = `${data.firstName} ${data.lastName}`.trim();

  const markOpened = (doc: 'terms' | 'privacy') => {
    setOpenedDocs((prev) => ({ ...prev, [doc]: true }));
  };

  const handleCheckboxChange = (field: keyof typeof agreements, checked: boolean) => {
    if (field === 'termsAccepted' && checked && !openedDocs.terms) return;
    if (field === 'privacyAccepted' && checked && !openedDocs.privacy) return;
    const next = { ...agreements, [field]: checked };
    setAgreements(next);
    onUpdate(next);
    if (checked && errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateAgreements = (): boolean => {
    const nextErrors: Record<string, string> = {};
    if (!agreements.termsAccepted) nextErrors.termsAccepted = 'Required';
    if (!agreements.privacyAccepted) nextErrors.privacyAccepted = 'Required';
    if (!agreements.creditCheckConsent) nextErrors.creditCheckConsent = 'Required';
    if (!agreements.infoTruthful) nextErrors.infoTruthful = 'Required';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmitClick = () => {
    const allAccepted =
      agreements.termsAccepted &&
      agreements.privacyAccepted &&
      agreements.creditCheckConsent &&
      agreements.infoTruthful;

    if (!allAccepted) {
      setShowConsentModal(true);
      return;
    }
    onSubmit();
  };

  const handleConfirmSubmit = () => {
    if (validateAgreements()) {
      setShowConsentModal(false);
      onSubmit();
    }
  };

  return (
    <div className="review-step">
      <div className="review-step__header">
        <h2 className="review-step__title">Review Your Information</h2>
        <p className="review-step__subtitle">
          Please review all your details carefully before submitting your application.
        </p>
      </div>

      <div className="review-step__notice">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4" />
          <path d="M12 8h.01" />
        </svg>
        Make sure all information is correct. You can go back and edit if needed.
      </div>

      <section className="review-quote">
        <div className="review-quote__main">
          <span className="review-quote__label">Loan amount</span>
          <strong className="review-quote__amount">{formatRand(data.loanAmount)}</strong>
          <span className="review-quote__due">Repay {formatRand(quote.totalAmount)} by {quote.repaymentDateLabel}</span>
        </div>
        <dl className="review-quote__breakdown">
          <div><dt>You get</dt><dd>{formatRand(data.loanAmount)}</dd></div>
          <div><dt>Interest</dt><dd>{formatRand(quote.interestAmount)}</dd></div>
          <div><dt>Admin fee</dt><dd>{formatRand(quote.adminFeeExclVat)}</dd></div>
          <div><dt>VAT</dt><dd>{formatRand(quote.vatAmount)}</dd></div>
          <div><dt>Initiation fee</dt><dd>{formatRand(quote.initiationFee)}</dd></div>
          <div className="review-quote__total"><dt>Total to repay</dt><dd>{formatRand(quote.totalAmount)}</dd></div>
        </dl>
      </section>

      <div className="review-step__grid">
        <ReviewCard title="Personal Details" tone="personal" onEdit={() => onEditStep(2)}>
          <ReviewField label="Full Name" value={`${data.firstName} ${data.lastName}`.trim()} />
          <ReviewField label="Email Address" value={data.email} />
          <ReviewField label="Phone Number" value={formatPhone(data.phoneNumber)} />
          <ReviewField label="ID Number" value={formatSaId(data.saIdNumber)} />
        </ReviewCard>

        <ReviewCard title="Bank Details" tone="bank" onEdit={() => onEditStep(3)}>
          <ReviewField label="Bank" value={data.bankName} />
          <ReviewField
            label="Account Number"
            value={`**** ${data.accountNumber.slice(-4)} (${data.accountType})`}
          />
          <ReviewField label="Branch Code" value={data.branchCode} />
          <ReviewField label="Account Holder" value={accountHolder} />
        </ReviewCard>

        <ReviewCard
          title="Institution Details"
          tone="institution"
          onEdit={() => onEditStep(4)}
          fullWidth
        >
          <ReviewField label="Institution" value={data.institutionName} />
          <ReviewField label="Student Number" value={data.studentNumber} />
          <ReviewField label="Year of study" value={data.studyYear} />
          <ReviewField
            label="Monthly NSFAS allowance"
            value={data.nsfasMonthlyAllowance ? `R${data.nsfasMonthlyAllowance}` : ''}
          />
          <ReviewField
            label="Other credit repayments"
            value={
              data.hasOtherCredit === 'yes'
                ? `Yes — R${data.otherCreditMonthly} / month`
                : data.hasOtherCredit === 'no'
                  ? 'No'
                  : ''
            }
          />
        </ReviewCard>
      </div>

      <footer className="review-step__footer">
        <div className="review-step__footer-inner">
          <div className="review-step__secure">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span>
              Your information is safe and secure. We use bank-level encryption to protect your data.
            </span>
          </div>

          <div className="review-step__loan">
            <div className="review-step__loan-badge">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                <path d="M9 12l2 2 4-4" />
                <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c1.51 0 2.93.37 4.18 1.03" />
              </svg>
            </div>
            <div>
              <div className="review-step__loan-amount">Loan Amount R{data.loanAmount}</div>
              <div className="review-step__loan-repay">To be repaid by {quote.repaymentDateLabel}: {formatRand(quote.totalAmount)}</div>
            </div>
          </div>

          <div className="review-step__actions">
            <div className="review-step__buttons">
              <button type="button" className="review-step__back" onClick={onBack} disabled={isSubmitting}>
                ← Back
              </button>
              <button
                type="button"
                className="review-step__submit"
                onClick={handleSubmitClick}
                disabled={isSubmitting}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
            <p className="review-step__terms">
              By submitting, you agree to our{' '}
              <Link to="/terms" target="_blank" rel="noopener noreferrer">
                Terms &amp; Conditions
              </Link>
            </p>
          </div>
        </div>
      </footer>

      {showConsentModal && (
        <div className="review-consent-modal" role="dialog" aria-modal="true">
          <div className="review-consent-modal__panel">
            <h3>Confirm before submitting</h3>
            <p>Please accept the required agreements to complete your application.</p>

            <div className="review-consent-item">
              <label>
                <input
                  type="checkbox"
                  checked={agreements.termsAccepted}
                  disabled={!openedDocs.terms}
                  onChange={(e) => handleCheckboxChange('termsAccepted', e.target.checked)}
                />
                <span>
                  I have read and accept the{' '}
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="review-consent-link"
                    onClick={() => markOpened('terms')}
                  >
                    loan agreement terms
                  </Link>
                </span>
              </label>
              {!openedDocs.terms && (
                <div className="review-consent-hint">Open the loan agreement terms before you can tick this.</div>
              )}
              {errors.termsAccepted && (
                <div className="review-consent-error">{errors.termsAccepted}</div>
              )}
            </div>

            <div className="review-consent-item">
              <label>
                <input
                  type="checkbox"
                  checked={agreements.privacyAccepted}
                  disabled={!openedDocs.privacy}
                  onChange={(e) => handleCheckboxChange('privacyAccepted', e.target.checked)}
                />
                <span>
                  I accept data processing and the{' '}
                  <Link
                    to="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="review-consent-link"
                    onClick={() => markOpened('privacy')}
                  >
                    privacy policy
                  </Link>
                </span>
              </label>
              {!openedDocs.privacy && (
                <div className="review-consent-hint">Open the privacy policy before you can tick this.</div>
              )}
              {errors.privacyAccepted && (
                <div className="review-consent-error">{errors.privacyAccepted}</div>
              )}
            </div>

            <div className="review-consent-item">
              <label>
                <input
                  type="checkbox"
                  checked={agreements.creditCheckConsent}
                  onChange={(e) => handleCheckboxChange('creditCheckConsent', e.target.checked)}
                />
                I consent to credit checks and information verification
              </label>
              {errors.creditCheckConsent && (
                <div className="review-consent-error">{errors.creditCheckConsent}</div>
              )}
            </div>

            <div className="review-consent-item">
              <label>
                <input
                  type="checkbox"
                  checked={agreements.infoTruthful}
                  onChange={(e) => handleCheckboxChange('infoTruthful', e.target.checked)}
                />
                I confirm all information provided is true and belongs to me
              </label>
              {errors.infoTruthful && (
                <div className="review-consent-error">{errors.infoTruthful}</div>
              )}
            </div>

            <div className="review-consent-modal__actions">
              <button type="button" className="review-consent-cancel" onClick={() => setShowConsentModal(false)}>
                Cancel
              </button>
              <button type="button" className="review-step__submit" onClick={handleConfirmSubmit}>
                Confirm &amp; Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step5TermsAndConditions;
